import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { twJoin } from "tailwind-merge";

import type {
  TicketCategoryDetailsDto,
  TicketCategoryTreeDto,
  TicketCategoryTreeNodeDto,
  TicketCategoryTreeNodeUpdateRequest,
} from "@/api/types";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { Id } from "@/components/Id/Id";
import { Card } from "@/design-system/Card";
import { Checkbox } from "@/design-system/Checkbox";
import { Inline } from "@/design-system/Inline";
import { Input } from "@/design-system/Input";
import { Notice } from "@/design-system/Notice";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { Switch } from "@/design-system/Switch";
import {
  useDisableTicketCategoryNode,
  useEditTicketCategoryNode,
  useEnableTicketCategoryNode,
} from "@/queries/ticket-categories/mutations";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { useTranslation } from "@/translations";
import { createTreeCollection, TreeView, useTreeView } from "@/ui-adapters/ArkTreeView";

import { searchTree } from "../helpers/tree-helpers";

type LocationType = TicketCategoryTreeNodeUpdateRequest["locationType"];

interface TicketCategoryTreeProps {
  ticketCategory: TicketCategoryDetailsDto;
  isTreeVveEnabled: boolean;
  treeOverrideExternalCode: string;
  onToggleTreeVve: (state: boolean) => void;
  onChangeTreeOverrideExternalCode: (value: string) => void;
}

export function TicketCategoryTree({
  ticketCategory,
  isTreeVveEnabled,
  treeOverrideExternalCode,
  onToggleTreeVve,
  onChangeTreeOverrideExternalCode,
}: TicketCategoryTreeProps): React.ReactNode {
  const queryOptions = useTicketCategoryQueries();
  const { data: tree } = useSuspenseQuery(queryOptions.treeDetails(ticketCategory.id));

  const editTicketCategoryNodeMutation = useEditTicketCategoryNode();
  const enableTicketCategoryNodeMutation = useEnableTicketCategoryNode();
  const disableTicketCategoryNodeMutation = useDisableTicketCategoryNode();

  return (
    <TreeContent
      tree={tree}
      isTreeVveEnabled={isTreeVveEnabled}
      treeOverrideExternalCode={treeOverrideExternalCode}
      onToggleTreeVve={onToggleTreeVve}
      onChangeTreeOverrideExternalCode={onChangeTreeOverrideExternalCode}
      onEditNode={(data) =>
        editTicketCategoryNodeMutation.mutate({
          categoryId: ticketCategory.id,
          node: {
            ...data.node,
            locationType: data.locationType,
          },
        })
      }
      onEnableNode={(node) => enableTicketCategoryNodeMutation.mutate({ categoryId: ticketCategory.id, node })}
      onDisableNode={(node) => disableTicketCategoryNodeMutation.mutate({ categoryId: ticketCategory.id, node })}
      onToggleNodeExternalOverrideCode={(data) =>
        editTicketCategoryNodeMutation.mutate({
          categoryId: ticketCategory.id,
          node: { ...data.node, useTreeOverrideExternalCode: data.state },
        })
      }
    />
  );
}

type TreeContentProps = {
  tree: TicketCategoryTreeDto;
  isTreeVveEnabled: boolean;
  treeOverrideExternalCode: string;
  onToggleTreeVve: (state: boolean) => void;
  onChangeTreeOverrideExternalCode: (value: string) => void;
  onEditNode: (data: { node: TicketCategoryTreeNodeDto; locationType: LocationType }) => void;
  onEnableNode: (data: TicketCategoryTreeNodeDto) => void;
  onDisableNode: (node: TicketCategoryTreeNodeDto) => void;
  onToggleNodeExternalOverrideCode: (data: { node: TicketCategoryTreeNodeDto; state: boolean }) => void;
};

function TreeContent({
  tree,
  isTreeVveEnabled,
  treeOverrideExternalCode,
  onToggleTreeVve,
  onChangeTreeOverrideExternalCode,
  onEditNode,
  onEnableNode,
  onDisableNode,
  onToggleNodeExternalOverrideCode,
}: TreeContentProps) {
  const { t } = useTranslation();
  const collection = useMemo(() => {
    const root = {
      id: "root",
      name: "",
      children: tree.nodes,
      enabled: true,
      isFinal: false,
      locationType: "insideOrOutside",
      useTreeOverrideExternalCode: Boolean(treeOverrideExternalCode),
    } satisfies TicketCategoryTreeNodeDto;

    const filteredTree = searchTree(root, "");

    return createTreeCollection<TicketCategoryTreeNodeDto>({
      nodeToValue: (node) => node.id,
      nodeToString: (node) => node.name,
      rootNode: filteredTree || {
        id: "root",
        name: "No results",
        children: [],
        enabled: true,
        isFinal: false,
        locationType: "insideOrOutside",
        useTreeOverrideExternalCode: Boolean(treeOverrideExternalCode),
      },
    });
  }, [tree.nodes, treeOverrideExternalCode]);

  const treeView = useTreeView({
    defaultExpandedValue: [collection.rootNode.id],
    collection,
    typeahead: false,
  });

  const handleToggleNode = (node: TicketCategoryTreeNodeDto) => {
    if (treeView.expandedValue.includes(node.id)) {
      // Unfortunately we need to include this logic because otherwise the tree collapses upon clicking the checkbox.
      // This has the side effect of opening the tree if it was closed, but we can live with that.
      treeView.expand([node.id]);
    }

    if (node.enabled) {
      onDisableNode(node);
    } else {
      onEnableNode(node);
    }
  };

  return (
    <Card>
      <Stack gap="xl">
        {/* VVE option */}
        <Stack gap="sm">
          <header>
            <h3 className="text-heading-3">{t("page.ticket-category-tree.title")}</h3>
            <p className="text-text-secondary">{t("page.ticket-category-tree.enable-vve-features.description")}</p>
          </header>
          <Checkbox
            name="treeVveEnabled"
            checked={isTreeVveEnabled}
            label={t("page.ticket-category-tree.enable-vve-features.label")}
            onChange={(e) => onToggleTreeVve(e.target.checked)}
          />
        </Stack>
        {/* Tree override external SD code (e.g. Warranty) */}
        <Stack gap="sm">
          <header>
            <h3 className="text-heading-3">{t("page.ticket-category-tree.override-external-code.title")}</h3>
            <p className="text-text-secondary">{t("page.ticket-category-tree.override-external-code.description")}</p>
          </header>
          <Input
            value={treeOverrideExternalCode || ""}
            name="treeOverrideExternalCode"
            onChange={(e) => onChangeTreeOverrideExternalCode(e.target.value)}
            placeholder={t("page.ticket-category-tree.override-external-code.placeholder")}
            width="standard"
          />
        </Stack>
        {/* Tree view */}
        <TreeView.RootProvider value={treeView}>
          <TreeView.Label className="flex flex-col gap-space-sm">
            <h4 className="text-heading-4">{t("page.ticket-category-tree.tree.title")}</h4>
            <Notice
              icon={<AlertTriangleIcon />}
              variant="warning"
              message={t("page.ticket-category-tree.tree.notice")}
            />
          </TreeView.Label>
          <TreeView.Tree>
            {collection.rootNode.children?.map((node, index) => (
              <TreeNode
                key={node.id}
                node={node}
                indexPath={[index]}
                externalOverrideCode={treeOverrideExternalCode}
                isVveEnabled={isTreeVveEnabled}
                onEdit={onEditNode}
                onToggle={handleToggleNode}
                onToggleExternalOverrideCode={treeOverrideExternalCode ? onToggleNodeExternalOverrideCode : undefined}
              />
            ))}
          </TreeView.Tree>
        </TreeView.RootProvider>
      </Stack>
    </Card>
  );
}

type TreeNodeProps = TreeView.NodeProviderProps<TicketCategoryTreeNodeDto> & {
  externalOverrideCode: string | undefined;
  isVveEnabled: boolean;
  onEdit: (data: { node: TicketCategoryTreeNodeDto; locationType: LocationType }) => void;
  onToggle: (node: TicketCategoryTreeNodeDto) => void;
  onToggleExternalOverrideCode?: (data: { node: TicketCategoryTreeNodeDto; state: boolean }) => void;
};

function TreeNode({
  node,
  indexPath,
  externalOverrideCode,
  isVveEnabled,
  onEdit,
  onToggle,
  onToggleExternalOverrideCode,
}: TreeNodeProps) {
  const { t } = useTranslation();

  // Determine which ID/Code to show based on VVE settings and node properties
  const defectId =
    node.locationType === "outside" && isVveEnabled && node.externalSecondaryCode
      ? node.externalSecondaryCode
      : node.externalCode;

  return (
    <TreeView.NodeProvider key={node.id} node={node} indexPath={indexPath}>
      {node.children && node.children.length > 0 && (
        <TreeView.Branch className="relative">
          <TreeView.BranchControl className="flex items-center py-space-sm">
            {!node.isFinal && (
              <TreeView.BranchIndicator className="flex items-center gap-space-sm *:data-[state=open]:rotate-90">
                <span className="mr-space-sm block transition-transform">
                  <ChevronRightIcon size={24} />
                </span>
              </TreeView.BranchIndicator>
            )}
            <TreeView.BranchText className="flex items-center gap-space-sm">
              <Checkbox checked={node.enabled} onChange={() => onToggle(node)} />
              {node.name}
            </TreeView.BranchText>
          </TreeView.BranchControl>
          <TreeView.BranchContent>
            <TreeView.BranchIndentGuide />
            {node.children.map((child, index, children) => (
              <div className="flex" key={child.id}>
                <TreeConnector isFinal={child.isFinal} isLastChild={index === children.length - 1} />
                <div className="min-w-0 flex-1">
                  <TreeNode
                    node={child}
                    indexPath={[...indexPath, index]}
                    onEdit={onEdit}
                    onToggle={onToggle}
                    externalOverrideCode={externalOverrideCode}
                    isVveEnabled={isVveEnabled}
                    onToggleExternalOverrideCode={onToggleExternalOverrideCode}
                  />
                </div>
              </div>
            ))}
          </TreeView.BranchContent>
        </TreeView.Branch>
      )}

      {node.isFinal && (
        <TreeView.Item className="py-space-sm">
          <TreeView.ItemText className="flex min-w-0 items-center">
            <span aria-hidden="true" className="block w-icon-prominent shrink-0" />
            <span aria-hidden="true" className="block w-space-sm shrink-0" />
            <Checkbox checked={node.enabled} onChange={() => onToggle(node)} />
            <section className="ml-space-sm flex min-w-0 flex-1 flex-wrap items-center justify-between gap-space-sm">
              <span>{node.name}</span>
              <Inline gap="sm" align="center">
                {externalOverrideCode && node.externalCode && onToggleExternalOverrideCode && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Switch
                      labels={{
                        off: t("page.ticket-category-tree.use-tree-override-external-code.label"),
                      }}
                      isChecked={node.useTreeOverrideExternalCode}
                      onChange={(e) => onToggleExternalOverrideCode?.({ node, state: e.target.checked })}
                      size="sm"
                    />
                  </div>
                )}
                {externalOverrideCode && node.externalCode && node.useTreeOverrideExternalCode && (
                  <Id>{externalOverrideCode}</Id>
                )}
                {node.externalCode && (
                  <span className={node.useTreeOverrideExternalCode ? "[&>pre]:line-through" : undefined}>
                    <Id>{defectId}</Id>
                  </span>
                )}
                <div className="w-control-narrow">
                  <Select
                    items={["inside", "outside", "insideOrOutside"] as const}
                    selected={node.locationType}
                    keySelector={(x) => x}
                    renderOption={(x) => {
                      switch (x) {
                        case "inside":
                          return t("page.ticket-category-tree.location-type.inside");
                        case "outside":
                          return t("page.ticket-category-tree.location-type.outside");
                        case "insideOrOutside":
                          return t("page.ticket-category-tree.location-type.inside-or-outside");
                      }
                    }}
                    onChange={(locationType) => onEdit({ node, locationType })}
                    placement="bottom-right"
                  />
                </div>
              </Inline>
            </section>
          </TreeView.ItemText>
        </TreeView.Item>
      )}
    </TreeView.NodeProvider>
  );
}

function TreeConnector({ isFinal, isLastChild }: { isFinal: boolean; isLastChild: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={twJoin("w-control-md shrink-0", isLastChild ? undefined : "border-l border-background-tertiary")}
    >
      {isFinal && <span className="block h-space-sm border-l border-background-tertiary" />}
      <svg
        className="h-control-md w-icon-prominent text-text-disabled"
        viewBox="0 0 24 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={isLastChild ? "M0 0V20H24" : "M0 20H24"} stroke="currentColor" />
      </svg>
    </div>
  );
}
